import {
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Checkbox } from "@ofqual/portal-components/components/Checkbox";

import {
  NonNullableOrganisationState,
  NonNullableRecognitionApplicationState,
} from "../../../redux/state";
import { fieldLabels } from "../../fieldLabels";
import { ApplicationAppBar } from "../ApplicationAppBar";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { countriesService } from "../../../services";
import React from "react";

const useStyles = makeStyles((theme) => ({
  formHeading: {
    color: "#9f9f9f",
    fontSize: "1em",
    marginBottom: "30px",
    fontWeight: 600,
    position: "relative",
  },
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
}));

type OrganisationProps = {
  orgState: NonNullableOrganisationState;
  applicationState: NonNullableRecognitionApplicationState;
  canEdit: boolean;
};

export const Organisation: React.FC<OrganisationProps> = ({
  orgState,
  applicationState,
  canEdit,
}) => {
  const classes = useStyles();
  const [countriesLookup] = useLookup(countriesService);

  const { organisation } = applicationState.application;
  return (
    <>
      <ApplicationAppBar
        orgState={orgState}
        applicationState={applicationState}
        tabName="Organisation Details"
        canEdit={canEdit}
      />
      <div data-test="organisation-details">
        <div id="data-panel">
          <Typography data-test="organisation-details-main-header" variant="h6">
            Main Details
          </Typography>
          <Paper
            className={classes.formSection}
            data-test="organisation-details-main-section"
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["name"]}
                  fullWidth
                  readOnly
                  value={organisation.name}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["legalName"]}
                  fullWidth
                  readOnly
                  value={organisation.legalName}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["acronym"]}
                  fullWidth
                  readOnly
                  value={organisation.acronym}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["email"]}
                  fullWidth
                  readOnly
                  value={organisation.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["website"]}
                  fullWidth
                  readOnly
                  value={organisation.website}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["listOfStandardFeesUrl"]}
                  fullWidth
                  readOnly
                  value={organisation.listOfStandardFeesUrl}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={organisation.hasCompanyNumber}
                      readOnly
                    />
                  }
                  label={fieldLabels["hasCompanyNumber"]}
                />
              </Grid>
              {organisation.hasCompanyNumber ? (
                <Grid item xs={12}>
                  <TextField
                    data-test="organisation-details-main-companyNumber"
                    variant="outlined"
                    label={fieldLabels["companyNumber"]}
                    fullWidth
                    readOnly
                    value={organisation.companyNumber}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={organisation.hasCharityNumber}
                      readOnly
                    />
                  }
                  label={fieldLabels["hasCharityNumber"]}
                />
              </Grid>
              {organisation.hasCharityNumber ? (
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    label={fieldLabels["charityNumber"]}
                    fullWidth
                    readOnly
                    value={organisation.charityNumber}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={organisation.isOtherUkLegalEntity}
                      readOnly
                    />
                  }
                  label={fieldLabels["isOtherUkLegalEntity"]}
                />
              </Grid>
              {organisation.isOtherUkLegalEntity ? (
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    label={fieldLabels["otherLegalEntityType"]}
                    fullWidth
                    readOnly
                    value={organisation.otherLegalEntityType}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      readOnly
                      checked={organisation.isRegisteredInOtherCountry}
                    />
                  }
                  label={fieldLabels["isRegisteredInOtherCountry"]}
                />
              </Grid>
              {organisation.isRegisteredInOtherCountry ? (
                <>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      label={fieldLabels["otherCountryOfRegistration"]}
                      fullWidth
                      readOnly
                      value={
                        countriesLookup.find(
                          (x) =>
                            x.id === organisation.otherCountryOfRegistration
                        )?.label
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      label={fieldLabels["otherCountryIdentifier"]}
                      fullWidth
                      value={organisation.otherCountryIdentifier}
                    />
                  </Grid>
                </>
              ) : null}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      readOnly
                      checked={organisation.isIndividualOrPartnership}
                    />
                  }
                  label={fieldLabels["isIndividualOrPartnership"]}
                />
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="h6">Organisation Address</Typography>

          <Paper className={classes.formSection}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["line1"]}
                  fullWidth
                  readOnly
                  value={organisation.line1}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["line2"]}
                  fullWidth
                  readOnly
                  value={organisation.line2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["line3"]}
                  fullWidth
                  readOnly
                  value={organisation.line3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["line4"]}
                  fullWidth
                  readOnly
                  value={organisation.line4}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["postCode"]}
                  fullWidth
                  readOnly
                  value={organisation.postCode}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["country"]}
                  fullWidth
                  readOnly
                  value={
                    countriesLookup.find((x) => x.id === organisation.countryId)
                      ?.label
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  label={fieldLabels["phoneNumber"]}
                  fullWidth
                  readOnly
                  value={organisation.phoneNumber}
                />
              </Grid>
            </Grid>
          </Paper>
        </div>
      </div>
    </>
  );
};
